// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";

import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import type {
  DepartmentSpecificScript,
  DepartmentSpecificScriptsResponse,
} from "@modules/service/reducer/department-specific-scripts/types";
import type {
  HierarchyAssignment,
  ApprovableContainer,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetDepartmentSpecificScripts,
  AddDepartmentSpecificScript,
  GetDepartmentSpecificScript,
  StartDepartmentSpecificScriptDraft,
  UpdateDepartmentSpecificScriptAssignments,
  UpdateDepartmentSpecificScript,
  DiscardDepartmentSpecificScriptDraft,
  CancelDepartmentSpecificScriptReview,
  CancelDepartmentSpecificScriptReviewAndDiscard,
  DuplicateDepartmentSpecificScript,
  SubmitDepartmentSpecificScriptForReview,
  DisableDepartmentSpecificScript,
} from "@modules/service/constants/actions";
import { ApprovalStateEnum } from "@modules/service/constants";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type ListResponse<T> = $Exact<{
  content: T,
  pagination: any,
}>;

type ItemResponse<T> = $Exact<T>;

type ListRequest = $Exact<{
  clientId: string,
}>;

export type DepartmentSpecificRequest = $Exact<{
  ...ListRequest,
  stableId: string,
}>;

export type DeparmentSpecificScriptSubmitForReviewRequest = $Exact<{
  ...DepartmentSpecificRequest,
  notes: string,
}>;

type DepartmentSpecificScriptDisableRequest = $Exact<{
  clientId: string,
  providerId: string,
  approvable: ?Object,
  isActive: ?boolean,
}>;

type DeparmentSpecificScriptUpdateRequest = $Exact<{
  ...DepartmentSpecificRequest,
  draft: DepartmentSpecificScript,
}>;

type AddDeparmentSpecificScriptRequest = $Exact<{
  clientId: string,
  draft: DepartmentSpecificScript,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onGetDepartmentSpecificScripts({
  payload,
}: Action<ListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<DepartmentSpecificScriptsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script`,
    );

    yield put(actionCreators.GetDepartmentSpecificScriptsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDepartmentSpecificScriptsError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetDepartmentSpecificScripts({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetDepartmentSpecificScript({
          clientId: params.clientId,
          stableId: params.stableId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

export function* onAddDepartmentSpecificScript({
  payload,
}: Action<AddDeparmentSpecificScriptRequest>): Generator<
  *,
  void,
  { data: DepartmentSpecificScript },
> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.AddDepartmentSpecificScriptSuccess()),
      put(actionCreators.GetDepartmentSpecificScriptActiveSuccess(response.data)),
      put(actionCreators.GetDepartmentSpecificScripts({ clientId: payload.clientId })),
      put(actionCreators.ShowAddDepartmentSpecificScriptModal({ show: false, step: 1 })),
      put(sendSuccessMessage("DEPARTMENT_SCRIPT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddDepartmentSpecificScriptError(e.response));
  }
}

export function* onGetDepartmentSpecificScript({
  payload,
}: Action<DepartmentSpecificRequest>): Generator<*, void, { data: DepartmentSpecificScript }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const stabeResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/assignment`,
    );

    yield put(actionCreators.GetDepartmentSpecificScriptSuccess(stabeResponse.data));
    yield put(actionCreators.SetDepartmentSpecificScriptAssignments(assignmentsResponse.data));
  } catch (e) {
    yield put(actionCreators.GetDepartmentSpecificScriptError(e.response));
  }
}

export function* onStartDepartmentSpecificScriptDraft({
  payload,
}: Action<DepartmentSpecificRequest>): Generator<*, void, { data: DepartmentSpecificScript }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}`,
    );

    yield all([
      put(actionCreators.StartDepartmentSpecificScriptDraftSuccess(response.data)),
      put(actionCreators.ShowDepartmentSpecificScriptEditModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDepartmentSpecificScriptDraftError(e.response));
  }
}

type DepartmentSpecificScriptUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<DepartmentSpecificScript>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateDepartmentSpecificScriptAssignments({
  payload,
}: Action<DepartmentSpecificScriptUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.container.id}/assignment`,
    );

    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.GetDepartmentSpecificScripts({ clientId: payload.container.clientId })),
      put(actionCreators.ShowEditDepartmentSpecificScriptAssignmentsModal(false)),
      put(sendSuccessMessage("HIERARCHY_SCOPE_UPDATED")),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.container.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDepartmentSpecificScriptAssignmentsError(e.message));
  }
}

export function* onUpdateDepartmentSpecificScript({
  payload,
}: Action<DeparmentSpecificScriptUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DepartmentSpecificScript>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield all([
      put(actionCreators.UpdateDepartmentSpecificScriptSuccess(response.data)),
      put(actionCreators.ShowDepartmentSpecificScriptEditModal(false)),
      put(actionCreators.GetDepartmentSpecificScripts({ clientId: payload.clientId })),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDepartmentSpecificScriptError(e.response));
  }
}

export function* onDiscardDepartmentSpecificScriptDraft({
  payload,
}: Action<DepartmentSpecificRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.DiscardDepartmentSpecificScriptDraftSuccess()),
      put(actionCreators.SetShowDiscardDepartmentSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_DISCARDED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DiscardDepartmentSpecificScriptDraftError(e.response));
  }
}

export function* onCancelDepartmentSpecificScriptReview({
  payload,
}: Action<DepartmentSpecificRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/cancel-review`,
    );

    yield all([
      put(actionCreators.CancelDepartmentSpecificScriptReviewSuccess()),
      put(actionCreators.SetShowCancelDepartmentSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_CANCELED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.CancelDepartmentSpecificScriptReviewError(e.response));
  }
}

export function* onCancelDepartmentSpecificScriptReviewAndDiscard({
  payload,
}: Action<DepartmentSpecificRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.CancelDepartmentSpecificScriptReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardDepartmentSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_CANCELED_AND_DISCARDED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.CancelDepartmentSpecificScriptReviewAndDiscardError(e.response));
  }
}

export function* onDuplicateDepartmentSpecificScript({
  payload,
}: Action<DepartmentSpecificRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DepartmentSpecificScript>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateDepartmentSpecificScriptSuccess(response.data)),
      put(actionCreators.SetDepartmentSpecificScriptAssignments([])),
      put(actionCreators.ShowAddDepartmentSpecificScriptModal({ show: true, step: 1 })),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateDepartmentSpecificScriptError(e.response));
  }
}

export function* onSubmitDepartmentSpecificScriptForReview({
  payload,
}: Action<DeparmentSpecificScriptSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.stableId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield all([
      put(actionCreators.SubmitDepartmentSpecificScriptForReviewSuccess()),
      put(actionCreators.SetShowSubmitDepartmentSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_SUBMITTED")),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitDepartmentSpecificScriptForReviewError(e.response));
  }
}

export function* onDisableDepartmentSpecificScript({
  payload,
}: Action<DepartmentSpecificScriptDisableRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.providerId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.providerId}/draft`,
      {
        ...draft,
        isActive: payload.isActive,
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/standard-script/hierarchy-script/${payload.providerId}/submit-for-review`,
      {
        notes: "Department specific script disabled and resubmitted for review",
      },
    );
    yield call(onRefresh, { clientId: payload.clientId });
    yield all([
      put(actionCreators.DisableDepartmentSpecificScriptSuccess(response.data)),
      put(actionCreators.SetShowDisableDepartmentSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
    ]);
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.DisableDepartmentSpecificScriptError(e.response));
  }
}

export function* DepartmentSpecificScriptsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDepartmentSpecificScripts, onGetDepartmentSpecificScripts),
    takeEvery(AddDepartmentSpecificScript, onAddDepartmentSpecificScript),
    takeEvery(GetDepartmentSpecificScript, onGetDepartmentSpecificScript),
    takeEvery(StartDepartmentSpecificScriptDraft, onStartDepartmentSpecificScriptDraft),
    takeEvery(
      UpdateDepartmentSpecificScriptAssignments,
      onUpdateDepartmentSpecificScriptAssignments,
    ),
    takeEvery(UpdateDepartmentSpecificScript, onUpdateDepartmentSpecificScript),
    takeEvery(DiscardDepartmentSpecificScriptDraft, onDiscardDepartmentSpecificScriptDraft),
    takeEvery(CancelDepartmentSpecificScriptReview, onCancelDepartmentSpecificScriptReview),
    takeEvery(
      CancelDepartmentSpecificScriptReviewAndDiscard,
      onCancelDepartmentSpecificScriptReviewAndDiscard,
    ),
    takeEvery(DuplicateDepartmentSpecificScript, onDuplicateDepartmentSpecificScript),
    takeEvery(SubmitDepartmentSpecificScriptForReview, onSubmitDepartmentSpecificScriptForReview),
    takeEvery(DisableDepartmentSpecificScript, onDisableDepartmentSpecificScript),
  ]);
}

export default DepartmentSpecificScriptsSagas;
